import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import useUser from 'common/hooks/useUser';

import { FileUpload, UploadError, concurrentFilesUploader } from './concurrentFilesUploader';
import { useAssetRepositoryPrepareAsset } from '../useAssetRepositoryPrepareAsset/useAssetRepositoryPrepareAsset';

type UploadProgress = { count: number; total: number };

export type UploadResult = {
  publishedImagesUrls: string[];
  uploadErrors: UploadError[];
};

const INITIAL_PROGRESS: UploadProgress = {
  count: 0,
  total: 0,
};

const useAssetRepositoryUpload = () => {
  const { user } = useUser();
  const [progress, setUploadProgress] = useState<UploadProgress>(INITIAL_PROGRESS);
  const { prepareAssets } = useAssetRepositoryPrepareAsset();

  const { mutateAsync: prepareAssetsMutation, isLoading } = useMutation(
    async (files: File[]): Promise<UploadResult> => {
      if (!user) {
        throw new Error('User must be authorized to upload assets');
      }
      if (files.length === 0) {
        return { publishedImagesUrls: [], uploadErrors: [] };
      }

      setUploadProgress({ count: 0, total: files.length });

      const publishedImagesUrls: string[] = [];

      const preparedAssets = await prepareAssets(files);

      const uploads: FileUpload[] = preparedAssets.map((preparedAsset, index) => ({
        file: files[index],
        uploadUrl: preparedAsset.s3PresignedUrl,
        onUploadFinished: () => {
          publishedImagesUrls.push(preparedAsset.imageResizerProxyUrl);
          setUploadProgress(progress => ({ ...progress, count: progress.count + 1 }));
        },
      }));

      // TODO: In the future this endpoint will have image size validation (min 720x405)
      const uploadErrors = await concurrentFilesUploader(uploads);

      return { publishedImagesUrls, uploadErrors };
    },
    {
      retry: 1,
    },
  );

  return {
    uploadAssets: prepareAssetsMutation,
    isLoading,
    progress,
  };
};

export default useAssetRepositoryUpload;
